import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DateTimePicker } from "@/components/ui/datetime-picker";
import { ChevronDown, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { SourceCombobox } from "@/components/review/SourceCombobox";
import { UserCombobox } from "@/components/capabilities/UserCombobox";
import {
  useBulkCreateCapabilityOverrides,
  useCapabilityRegistry,
  useCapabilityTemplates,
} from "@/hooks/useCapabilities";
import { useScrapersSources } from "@/hooks/useScrapersSources";
import {
  SCOPE_TYPE_OPTIONS,
  OVERRIDE_STATE_OPTIONS,
} from "@/types/capabilities";
import type {
  CapabilityOverrideState,
  CapabilityScopeType,
} from "@/services/capabilitiesApi";
import { ApiError } from "@/services/api";

const NO_TEMPLATE = "__none__";

interface SelectedOverride {
  capability: string;
  state: CapabilityOverrideState;
}

interface CapabilityOverrideFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValues?: Partial<{
    capability: string;
    scope_type: CapabilityScopeType;
    scope_id: string;
    /** @deprecated Prefer selecting capabilities in the multi-cap list. */
    state: CapabilityOverrideState;
  }>;
}

const EMPTY_SHARED = {
  templateId: NO_TEMPLATE,
  scope_type: "global" as CapabilityScopeType,
  scope_id: "",
  reason: "",
  expires_at: "",
};

function denyLabelFor(
  scopeType: CapabilityScopeType,
  kind: string | undefined,
): string {
  return scopeType === "global" && kind === "ops" ? "Kill" : "Deny";
}

export function CapabilityOverrideFormDialog({
  open,
  onOpenChange,
  initialValues,
}: CapabilityOverrideFormDialogProps) {
  const [shared, setShared] = useState(EMPTY_SHARED);
  const [overrides, setOverrides] = useState<SelectedOverride[]>([]);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const initialCapability = initialValues?.capability;
    setShared({
      ...EMPTY_SHARED,
      scope_type: initialValues?.scope_type ?? EMPTY_SHARED.scope_type,
      scope_id: initialValues?.scope_id ?? "",
    });
    setOverrides(
      initialCapability
        ? [
            {
              capability: initialCapability,
              state: initialValues?.state ?? "deny",
            },
          ]
        : [],
    );
    setAddOpen(false);
  }, [open, initialValues]);

  const { data: registry = [], isLoading: registryLoading } =
    useCapabilityRegistry();
  const { data: templates = [], isLoading: templatesLoading } =
    useCapabilityTemplates();
  const { data: sources = [] } = useScrapersSources();
  const bulkMutation = useBulkCreateCapabilityOverrides();

  const registryByCapability = useMemo(() => {
    const map = new Map(registry.map((entry) => [entry.capability, entry]));
    return map;
  }, [registry]);

  const selectedCapabilities = useMemo(
    () => new Set(overrides.map((item) => item.capability)),
    [overrides],
  );

  const availableToAdd = useMemo(
    () =>
      registry.filter((entry) => !selectedCapabilities.has(entry.capability)),
    [registry, selectedCapabilities],
  );

  const nonSourceScopable = useMemo(() => {
    if (shared.scope_type !== "source") return [];
    return overrides.filter((item) => {
      const spec = registryByCapability.get(item.capability);
      return spec != null && !spec.source_scopable;
    });
  }, [overrides, registryByCapability, shared.scope_type]);

  const setSharedField =
    <K extends keyof typeof EMPTY_SHARED>(key: K) =>
    (value: (typeof EMPTY_SHARED)[K]) =>
      setShared((prev) => ({ ...prev, [key]: value }));

  const handleTemplateChange = (templateId: string) => {
    if (templateId === NO_TEMPLATE) {
      setShared((prev) => ({ ...prev, templateId: NO_TEMPLATE }));
      return;
    }

    const template = templates.find((entry) => entry.id === templateId);
    if (!template) return;

    setShared((prev) => ({
      ...prev,
      templateId,
      scope_type: template.suggested_scope_type,
      scope_id:
        prev.scope_type === template.suggested_scope_type ? prev.scope_id : "",
    }));
    setOverrides(
      template.overrides.map((item) => ({
        capability: item.capability,
        state: item.state,
      })),
    );
  };

  const addCapability = (capability: string) => {
    setOverrides((prev) => {
      if (prev.some((item) => item.capability === capability)) return prev;
      return [...prev, { capability, state: "deny" }];
    });
  };

  const removeCapability = (capability: string) => {
    setOverrides((prev) =>
      prev.filter((item) => item.capability !== capability),
    );
  };

  const setCapabilityState = (
    capability: string,
    state: CapabilityOverrideState,
  ) => {
    setOverrides((prev) =>
      prev.map((item) =>
        item.capability === capability ? { ...item, state } : item,
      ),
    );
  };

  const handleSubmit = async () => {
    if (overrides.length === 0 || !shared.reason.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (shared.scope_type !== "global" && !shared.scope_id) {
      toast.error("Select a source or user for this scope");
      return;
    }

    try {
      const result = await bulkMutation.mutateAsync({
        scope_type: shared.scope_type,
        scope_id: shared.scope_type === "global" ? "" : shared.scope_id,
        reason: shared.reason.trim(),
        overrides: overrides.map((item) => ({
          capability: item.capability,
          state: item.state,
        })),
        ...(shared.expires_at
          ? { expires_at: new Date(shared.expires_at).toISOString() }
          : {}),
      });

      const noopCount = result.results.filter((row) => !row.changed).length;
      if (noopCount > 0) {
        toast.message(
          noopCount === result.results.length
            ? "Saved — but these rows don't change the current decision. Did you mean Inherit?"
            : `Saved — ${noopCount} of ${result.results.length} rows don't change the current decision.`,
        );
      } else {
        toast.success(
          result.results.length === 1
            ? "Override created"
            : `${result.results.length} overrides created`,
        );
      }
      onOpenChange(false);
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        toast.error(
          error.message ||
            "There is already an override at this capability/scope. Delete it first, then create the new one.",
        );
        return;
      }
      toast.error(error instanceof Error ? error.message : "An error occurred");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Override</DialogTitle>
          <DialogDescription>
            Changing a decision later is delete-then-recreate — this form only
            creates rows. Inherit is not a state; it's the absence of a row.
            Shared scope, reason, and expiry apply to every capability below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="template">Template (optional)</Label>
            <Select
              value={shared.templateId}
              onValueChange={handleTemplateChange}
            >
              <SelectTrigger id="template" className="mt-1">
                <SelectValue
                  placeholder={templatesLoading ? "Loading..." : "No template"}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_TEMPLATE}>No template</SelectItem>
                {templates.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {shared.templateId !== NO_TEMPLATE &&
              (() => {
                const selected = templates.find(
                  (t) => t.id === shared.templateId,
                );
                return selected?.description ? (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {selected.description}
                  </p>
                ) : null;
              })()}
          </div>

          <div>
            <Label htmlFor="scope_type">
              Scope <span className="text-destructive">*</span>
            </Label>
            <Select
              value={shared.scope_type}
              onValueChange={(value) => {
                setShared((prev) => ({
                  ...prev,
                  scope_type: value as CapabilityScopeType,
                  scope_id: "",
                }));
              }}
            >
              <SelectTrigger id="scope_type" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCOPE_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {shared.scope_type === "source" && (
            <div>
              <Label>
                Source <span className="text-destructive">*</span>
              </Label>
              <div className="mt-1">
                <SourceCombobox
                  sources={sources}
                  value={shared.scope_id || undefined}
                  onValueChange={(v) => setSharedField("scope_id")(v ?? "")}
                  placeholder="Select source..."
                  className="w-full"
                />
              </div>
              {nonSourceScopable.length > 0 && (
                <p className="mt-1 text-xs text-destructive">
                  {nonSourceScopable.length === 1
                    ? `${nonSourceScopable[0]!.capability} is not source-scopable. The write will fail.`
                    : `${nonSourceScopable.length} selected capabilities are not source-scopable. The write will fail.`}
                </p>
              )}
            </div>
          )}

          {shared.scope_type === "user" && (
            <div>
              <Label>
                User <span className="text-destructive">*</span>
              </Label>
              <div className="mt-1">
                <UserCombobox
                  value={shared.scope_id || undefined}
                  onValueChange={(v) => setSharedField("scope_id")(v ?? "")}
                  className="w-full"
                />
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="reason">
              Reason <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="reason"
              className="mt-1"
              placeholder="Why is this override needed?"
              value={shared.reason}
              onChange={(e) => setSharedField("reason")(e.target.value)}
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="expires_at">Expires (optional)</Label>
            <div className="mt-1">
              <DateTimePicker
                id="expires_at"
                value={shared.expires_at}
                onChange={setSharedField("expires_at")}
                placeholder="Pick date and time"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label>
                Capabilities <span className="text-destructive">*</span>
              </Label>
              <Popover open={addOpen} onOpenChange={setAddOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={registryLoading || availableToAdd.length === 0}
                  >
                    <Plus className="mr-1 size-3.5" />
                    Add
                    <ChevronDown className="ml-1 size-3.5 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-96 p-2" align="end">
                  <div className="max-h-64 space-y-0.5 overflow-y-auto">
                    {availableToAdd.length === 0 ? (
                      <p className="px-2 py-1.5 text-sm text-muted-foreground">
                        All capabilities selected
                      </p>
                    ) : (
                      availableToAdd.map((entry) => (
                        <button
                          key={entry.capability}
                          type="button"
                          className="flex w-full flex-col items-start rounded-sm px-2 py-1.5 text-left hover:bg-accent hover:text-accent-foreground"
                          onClick={() => {
                            addCapability(entry.capability);
                            setAddOpen(false);
                          }}
                        >
                          <span className="text-sm font-medium">
                            {entry.capability}
                            {entry.sold_manually ? " (sold manually)" : ""}
                          </span>
                          {entry.description ? (
                            <span className="text-xs text-muted-foreground line-clamp-2">
                              {entry.description}
                            </span>
                          ) : null}
                        </button>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {overrides.length === 0 ? (
              <p className="rounded-md border border-dashed px-3 py-4 text-sm text-muted-foreground">
                Add at least one capability, or pick a template.
              </p>
            ) : (
              <ul className="divide-y rounded-md border">
                {overrides.map((item) => {
                  const spec = registryByCapability.get(item.capability);
                  return (
                    <li
                      key={item.capability}
                      className="flex items-start gap-3 px-3 py-2.5"
                    >
                      <Checkbox
                        className="mt-1"
                        checked
                        onCheckedChange={(checked) => {
                          if (!checked) removeCapability(item.capability);
                        }}
                        aria-label={`Remove ${item.capability}`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium">
                          {item.capability}
                          {spec?.sold_manually ? " (sold manually)" : ""}
                        </div>
                        {spec?.description ? (
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {spec.description}
                          </p>
                        ) : null}
                      </div>
                      <Select
                        value={item.state}
                        onValueChange={(value) =>
                          setCapabilityState(
                            item.capability,
                            value as CapabilityOverrideState,
                          )
                        }
                      >
                        <SelectTrigger
                          className="mt-0.5 h-8 w-28 shrink-0"
                          aria-label={`State for ${item.capability}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {OVERRIDE_STATE_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.value === "deny"
                                ? denyLabelFor(shared.scope_type, spec?.kind)
                                : opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="mt-0.5 size-8 shrink-0"
                        onClick={() => removeCapability(item.capability)}
                        aria-label={`Remove ${item.capability}`}
                      >
                        <X className="size-4" />
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSubmit()}
            disabled={bulkMutation.isPending}
          >
            {bulkMutation.isPending && (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            )}
            Create Override
            {overrides.length > 1 ? `s (${overrides.length})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
